import { datadogRum } from "@datadog/browser-rum";
import { decamelizeKeys } from "humps";
import { isRole } from "PFApp/activities/helpers";
import { TemplateKey } from "PFApp/constants/templates";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { Select } from "PFComponents/select/select";
import Toggle from "PFComponents/toggle/toggle";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useActivityVacancies } from "PFCore/hooks/queries/activity";
import { useActivityClose } from "PFCore/hooks/queries/activity/use_activity_close";
import { useAdminTargetAllocationInvalidate } from "PFCore/hooks/queries/admin/target_allocations/use_target_allocation_invalidate";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useTargetAllocationInvalidate } from "PFCore/hooks/queries/target_allocation/use_target_allocation_invalidate";
import { Activity, AuditRole, ClosureReasonKey, Engagement, Role, Snakify } from "PFCore/types";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { humanize } from "underscore.string";

import css from "./close_confirm_modal.module.scss";

type CloseConfirmModalProps = {
  activity: Activity | AuditRole | Role | Engagement;
  handleClose: VoidFunction;
  handleSuccess: VoidFunction;
  type: TemplateKey;
};

export const CloseConfirmModal = ({
  activity: activityData,
  type,
  handleClose,
  handleSuccess
}: CloseConfirmModalProps) => {
  const { t } = useTranslation("activities");
  const { t: tTranslation } = useTranslation();
  const { data: myAccountInfo } = useMyAccountInfo();
  const { invalidateAllAllocationWindows } = useTargetAllocationInvalidate();
  const { invalidateAllRoles: invalidateAllAllocationWindowRoles, invalidateAllFallbackRoles } =
    useAdminTargetAllocationInvalidate();

  const { showGrowl } = useGrowlStoreActions();
  const { mutateAsync: resetShortlist } = useShortlistReset({}, { disabled: true });
  const { mutateAsync: closeActivity } = useActivityClose();

  const [selectedReason, setSelectedReason] = useState<ClosureReasonKey | null>(null);
  const [deleteBookings, setDeleteBookings] = useState(false);

  const activity = decamelizeKeys(activityData) as Activity | Snakify<AuditRole>; // TODO: [SP-2511] activityData might come from different endpoints, camelized or not

  const template = myAccountInfo.templates.find(
    (template) => template.id === activity.template_id || template.key === type
  );
  const filledOrBooked = activity.workflow_state === "booked" || activity.workflow_state === "filled";
  const activityType = humanize(template?.key || "").toLowerCase();
  const isRoleOrAuditRole = isRole(template);
  const isAuditRole = template?.key === TemplateKey.AuditRole;

  const { data: vacancies } = useActivityVacancies({ activityId: activity.id });
  const { data: shortlists, isFetching: isShortlistsFetching } = useActivityShortlists(
    activity.id,
    { source: "close_modal" }, // to not trigger refreshing shortlists list when opening the modal
    {
      enabled: !!activity.id && filledOrBooked && isAuditRole
    }
  );

  const closeOptions = useMemo(
    () =>
      (vacancies?.meta.availableClosureReasons || [])
        .filter(({ hidden, massClose }) => !hidden && massClose !== false)
        .map((option) => ({
          id: option.key,
          displayElement: option.label,
          item: option.key
        })),
    [vacancies?.meta]
  );

  const closeReasons = useMemo(
    () =>
      vacancies?.meta.availableClosureReasons?.reduce(
        (acc, val) => ({
          ...acc,
          [val.key]: val.label
        }),
        {}
      ) || {},
    [vacancies?.meta]
  );

  const closeVacanciesAndActivity = async () => {
    try {
      if (deleteBookings) {
        await Promise.all((shortlists?.entries || []).map((shortlist) => resetShortlist(shortlist.id)));
      }
      await closeActivity({ activityId: activity.id, vacanciesClosureReason: selectedReason });

      if (isAuditRole) {
        invalidateAllAllocationWindowRoles();
        invalidateAllAllocationWindows();
        invalidateAllFallbackRoles();
      }

      showGrowl({ message: t("closeActivity.success") });
      datadogRum.addAction("activity-list-close-task", {
        activityId: activity.id
      });
      handleSuccess && handleSuccess();
    } catch {
      showGrowl({ message: t("closeActivity.fail"), kind: "error" });
    }
  };

  const openVacancies = vacancies?.meta.totalOpened ?? 0;
  const hasOpenVacancies = isRoleOrAuditRole && !!vacancies && openVacancies > 0;
  const selectedCloseReason = selectedReason && closeReasons[selectedReason];
  const isSubmitDisabled = (isAuditRole && hasOpenVacancies && !selectedCloseReason) || isShortlistsFetching;

  return !!vacancies || !isRoleOrAuditRole ? (
    <Modal
      title={t("closeActivity.close", { activityType })}
      onOK={isSubmitDisabled ? undefined : closeVacanciesAndActivity}
      onClose={handleClose}
      labelOK={
        hasOpenVacancies && isRoleOrAuditRole
          ? t("closeActivity.goToVacancies", {
              activityType,
              count: openVacancies
            })
          : t("closeActivity.close", { activityType })
      }
      kindOK="danger"
      showCancelButton={false}
      classes={{
        modal: css.modal
      }}
    >
      {hasOpenVacancies && (
        <div className={css.header}>
          {t(`closeActivity.with_vacancies.title.${template?.key}` as unknown as TemplateStringsArray, {
            count: openVacancies
          })}
        </div>
      )}
      <div className={css.text}>
        {hasOpenVacancies ? (
          <Trans
            i18nKey={`closeActivity.with_vacancies.text.${template?.key}` as any}
            t={t}
            components={[
              <Link key={0} onClick={() => handleClose()} to={`/activities/${activity.id}/vacancies`} />
            ]}
          />
        ) : (
          t("closeActivity.text", { activityType })
        )}
        {hasOpenVacancies && isRoleOrAuditRole && (
          <>
            <div className={css.actions}>
              <div className={css.reasons}>{t("closeActivity.reason")}</div>
            </div>
            <Select
              className={css.selectReason}
              options={closeOptions}
              value={selectedReason ? closeReasons[selectedReason] : undefined}
              controlledValue
              onChange={(selected) => setSelectedReason(selected)}
            />
          </>
        )}
        {filledOrBooked && isAuditRole && (
          <div>
            {isShortlistsFetching && <LoadingDots />}
            {!isShortlistsFetching && !!shortlists && (
              <>
                <div className={css.bookedOrFilled}>
                  {t("closeActivity.bookedOrFilled", { activityType })}
                </div>
                <div className={css.toggleWrapper}>
                  <div>{tTranslation("no")}</div>
                  <Toggle inline checked={deleteBookings} onChange={setDeleteBookings} />
                  <div>{tTranslation("yes")}</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  ) : null;
};
